import { Button } from "@/ds/shadcn/components/ui/button";
import { Checkbox } from "@/ds/shadcn/components/ui/checkbox";
import { useCallback, useId, useMemo, useState, type FC, type JSX } from "react";
import { CameraIcon, LogsIcon } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { useCreateShortToken } from "@/features/common/api/useCreateShortToken";
import { API_BASE_URL } from "@/lib/common/config";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { SubscriptionGate } from "@/features/common/components/SubscriptionGate";
import { SectionHeader } from "@/ds/custom/SectionHeader";
import { SectionRow } from "@/ds/custom/SectionRow";
import { useEffectOnFirstMount } from "@/lib/common/useEffectOnFirstMount";

type ExportOption = "Stateful" | "Transactional";

export const PageCollectionExport: FC = () => {
	const params = useParams();
	const collectionId = params.collectionId as string;

	return (
		<SubscriptionGate featureName="CSV Export">
			<PageCollectionExportInternal collectionId={collectionId} />
		</SubscriptionGate>
	);
};

interface PageCollectionExportInternalProps {
	collectionId: string;
}

const PageCollectionExportInternal: FC<PageCollectionExportInternalProps> = props => {
	const { collectionId } = props;
	const createShortToken = useCreateShortToken();
	const [exportOption, setExportOption] = useState<ExportOption | null>(null);
	const navigate = useNavigate();

	useEffectOnFirstMount(() => {
		createShortToken.mutate();

		const timer = setTimeout(() => navigate(".."), 9 * 60 * 60 * 1000);
		return () => clearTimeout(timer);
	});

	return (
		<SplitScreen fixed visible={!!exportOption} onVisibleChange={visible => !visible && setExportOption(null)}>
			<SplitScreenLeftPanel>
				<ExportOptionsDisplay
					exportOptionType={exportOption}
					onCheckedChange={setExportOption} />
			</SplitScreenLeftPanel>
			<SplitScreenRightPanel>
				<SectionHeader title="Select the fields you want to export" />

				{
					exportOption === "Stateful" &&
					<ExportStateful
						collectionId={collectionId}
						shortToken={createShortToken.data?.body.shortToken} />
				}

				{
					exportOption === "Transactional" &&
					<ExportTransactional
						collectionId={collectionId}
						shortToken={createShortToken.data?.body.shortToken} />
				}
			</SplitScreenRightPanel>
		</SplitScreen>
	);
};

interface ExportProps {
	collectionId: string | undefined;
	shortToken: string | undefined;
}

const ExportStateful = (props: ExportProps): JSX.Element => {
	const {
		collectionId,
		shortToken
	} = props;

	const [quantity, setQuantity] = useState(true);
	const [fcId, setFcId] = useState(true);
	const [description, setDescription] = useState(true);
	const [rarity, setRarity] = useState(true);
	const [totalValue, setTotalValue] = useState(true);
	const [unitValue, setUnitValue] = useState(true);
	const [includeTotals, setIncludeTotals] = useState(true);

	const getFieldList = useCallback(() => {
		const fieldList = [];

		if (!quantity) {
			fieldList.push("quantity");
		}
		if (!fcId) {
			fieldList.push("fcId");
		}
		if (!description) {
			fieldList.push("description");
		}
		if (!rarity) {
			fieldList.push("rarity");
		}
		if (!totalValue) {
			fieldList.push("totalValue");
		}
		if (!unitValue) {
			fieldList.push("unitValue");
		}

		return fieldList.join(",");
	}, [quantity, fcId, description, rarity, totalValue, unitValue]);

	const exportLink = useMemo(() => {
		return `${API_BASE_URL}/collection/${collectionId}/csv/stateful?omit=${getFieldList()}&shortToken=${shortToken}&includeTotals=${includeTotals}`;
	}, [collectionId, shortToken, includeTotals, getFieldList]);

	return (
		<>
			<ExportOptionDisplay
				title="Quantity"
				checked={quantity}
				onCheckedChange={() => setQuantity(!quantity)} />

			<ExportOptionDisplay
				title="Identifier"
				checked={fcId}
				onCheckedChange={() => setFcId(!fcId)} />

			<ExportOptionDisplay
				title="Description"
				checked={description}
				onCheckedChange={() => setDescription(!description)} />

			<ExportOptionDisplay
				title="Rarity"
				checked={rarity}
				onCheckedChange={() => setRarity(!rarity)} />

			<ExportOptionDisplay
				title="Unit Value"
				checked={unitValue}
				onCheckedChange={() => setUnitValue(!unitValue)} />

			<ExportOptionDisplay
				title="Total Value"
				checked={totalValue}
				onCheckedChange={() => setTotalValue(!totalValue)} />

			<ExportOptionDisplay
				title="Include Totals"
				description="When enabled, a final row of total values will be added to the CSV for any numeric columns.  The total in the Unit Market Value column will be the same as the Total Market Value."
				checked={includeTotals}
				onCheckedChange={() => setIncludeTotals(!includeTotals)} />

			<ConfirmExportButton
				disabled={(!quantity && !fcId && !description && !rarity && !totalValue && !unitValue) || !shortToken}
				link={exportLink} />
		</>
	);
};

const ExportTransactional = (props: ExportProps): JSX.Element => {
	const {
		collectionId,
		shortToken
	} = props;

	const [quantity, setQuantity] = useState(true);
	const [fcId, setFcId] = useState(true);
	const [description, setDescription] = useState(true);
	const [action, setAction] = useState(true);
	const [unitAmount, setUnitAmount] = useState(true);
	const [currency, setCurrency] = useState(true);
	const [date, setDate] = useState(true);

	const getFieldList = useCallback(() => {
		const fieldList = [];
		if (!quantity) {
			fieldList.push("quantity");
		}
		if (!fcId) {
			fieldList.push("fcId");
		}
		if (!description) {
			fieldList.push("description");
		}
		if (!action) {
			fieldList.push("action");
		}
		if (!unitAmount) {
			fieldList.push("unitAmount");
		}
		if (!currency) {
			fieldList.push("currency");
		}
		if (!date) {
			fieldList.push("date");
		}
		return fieldList.join(",");
	}, [quantity, fcId, description, action, unitAmount, currency, date ]);

	const exportLink = useMemo(() => {
		return `${API_BASE_URL}/collection/${collectionId}/csv/transactional?omit=${getFieldList()}&shortToken=${shortToken}`;
	}, [collectionId, shortToken, getFieldList]);

	return (
		<>
			<ExportOptionDisplay
				title="Quantity"
				checked={quantity}
				onCheckedChange={() => setQuantity(!quantity)} />

			<ExportOptionDisplay
				title="Identifier"
				checked={fcId}
				onCheckedChange={() => setFcId(!fcId)} />

			<ExportOptionDisplay
				title="Description"
				checked={description}
				onCheckedChange={() => setDescription(!description)} />

			<ExportOptionDisplay
				title="Action (Purchase or Sale)"
				checked={action}
				onCheckedChange={() => setAction(!action)} />

			<ExportOptionDisplay
				title="Unit Amount"
				checked={unitAmount}
				onCheckedChange={() => setUnitAmount(!unitAmount)} />

			<ExportOptionDisplay
				title="Currency"
				checked={currency}
				onCheckedChange={() => setCurrency(!currency)} />

			<ExportOptionDisplay
				title="Date"
				checked={date}
				onCheckedChange={() => setDate(!date)} />

			<ConfirmExportButton
				disabled={(!quantity && !fcId && !description && !action && !unitAmount && !currency && !date) || !shortToken}
				link={exportLink} />
		</>
	);
};

interface ExportOptionsProps {
	exportOptionType: ExportOption | null;
	onCheckedChange: (value: ExportOption) => void;
}

const ExportOptionsDisplay = (props: ExportOptionsProps): JSX.Element => {
	const {
		exportOptionType,
		onCheckedChange
	} = props;

	const statefulExportOption = useMemo(() => {
		return exportOptionType === "Stateful";
	}, [exportOptionType]);

	const transactionalExportOption = useMemo(() => {
		return exportOptionType === "Transactional";
	}, [exportOptionType]);

	return (
		<>
			<SectionRow
				icon={CameraIcon}
				title="Snapshot"
				description="Export what is in my collection right now (best for sharing)"
				selected={statefulExportOption}
				onClick={() => onCheckedChange("Stateful")} />
			<SectionRow
				icon={LogsIcon}
				title="Transactional"
				description="Export all transactions in my collection (best for offline editing and re-importing)"
				selected={transactionalExportOption}
				onClick={() => onCheckedChange("Transactional")} />
		</>
	);
};

interface ExportOptionDisplayProps {
	title: string;
	description?: string;
	checked: boolean;
	onCheckedChange: () => void;
}

const ExportOptionDisplay = (props: ExportOptionDisplayProps) => {
	const {
		title,
		description,
		checked,
		onCheckedChange
	} = props;

	const id = useId();

	return (
		<SectionRow
			title={<label htmlFor={id} className="cursor-pointer">{title}</label>}
			description={description}>
			<Checkbox
				id={id}
				className="text-primary"
				checked={checked}
				onCheckedChange={onCheckedChange} />
		</SectionRow>
	);
};

interface ConfirmExportButtonProps {
	disabled: boolean;
	link: string;
}

const ConfirmExportButton = (props: ConfirmExportButtonProps): JSX.Element => {
	const {
		disabled,
		link
	} = props;

	const navigate = useNavigate();
	const handleOnClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
		if (disabled) {
			e.preventDefault();
			return;
		}

		navigate("..");
	}, [disabled, navigate]);

	return (
		<div className="p-3 @md:flex @md:justify-end">
			<Link
				to={link}
				rel="noopener noreferrer"
				target="_blank"
				onClick={handleOnClick}>

				<Button className="w-full @md:w-32" disabled={disabled} variant="outline">
					Confirm Export
				</Button>
			</Link>
		</div>
	);
};
